import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { RetryErrorAlert } from "components/common/alerts/RetryErrorAlert";
import { useTenantData } from "contexts/tenant";
import { fetchErrorDetail } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useState } from "react";
import {
  HistoricalHeatOverride,
  HeatOverrideRecipe,
  useEditHeatOverrideHistoryMutation,
  useListHeatOverrideReasonsQuery,
  useListHeatOverrideRecipesQuery,
} from "src/store/api/generatedApi";
import { OverrideRecipeCard } from "./OverrideRecipeCard";
import {
  OverrideReasonFields,
  subreasonOptionsFor,
} from "./OverrideReasonFields";

export const EditHistoricalOverrideDialog = ({
  override,
  onClose,
  onSaved,
}: {
  override: HistoricalHeatOverride;
  onClose: () => void;
  onSaved: (override: HistoricalHeatOverride) => void;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { heat_override_options: heatOverrideOptions } = useTenantData();
  const [selectedRecipe, setSelectedRecipe] =
    useState<HeatOverrideRecipe | null>(null);
  const [reason, setReason] = useState(override.reason);
  const [subreason, setSubreason] = useState(override.subreason);
  const [comment, setComment] = useState(override.comment ?? "");
  const commentChanged = comment !== (override.comment ?? "");
  const commentRequired =
    heatOverrideOptions.includes("comment_required") && commentChanged;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const recipes = useListHeatOverrideRecipesQuery({ tenantName });
  const reasons = useListHeatOverrideReasonsQuery({ tenantName });
  const [save, saveState] = useEditHeatOverrideHistoryMutation();
  const recipeOptions = (recipes.data?.items ?? []).filter(
    (recipe) =>
      recipe.mix_id !== override.override_mix.id &&
      override.scopes.every((scope) =>
        recipe.steel_grades?.includes(scope.expected_grade)
      )
  );
  const reasonOptions = reasons.data?.reasons ?? [];
  const savedReason = reasonOptions.find(
    (option) => option.reason === override.reason
  );
  const savedSubreason = override.subreason;
  const displayedReasons =
    override.reason === null
      ? reasonOptions
      : savedReason === undefined
        ? [
            ...reasonOptions,
            {
              reason: override.reason,
              subreasons:
                override.subreason === null ? [] : [override.subreason],
            },
          ]
        : savedSubreason !== null &&
            !savedReason.subreasons.includes(savedSubreason)
          ? reasonOptions.map((option) =>
              option === savedReason
                ? {
                    ...option,
                    subreasons: [...option.subreasons, savedSubreason],
                  }
                : option
            )
          : reasonOptions;
  const reasonUnchanged =
    reason === override.reason && subreason === override.subreason;
  const reasonComplete =
    reasonUnchanged ||
    (reason !== null &&
      (subreasonOptionsFor({ reasonOptions: displayedReasons, reason })
        .length === 0 ||
        subreason !== null));
  const changed = selectedRecipe !== null || !reasonUnchanged || commentChanged;
  const canSave =
    changed &&
    reasonComplete &&
    (!commentRequired || comment.trim().length > 0);
  const submit = async () => {
    setSubmitError(null);
    const response = await save({
      tenantName,
      overrideId: override.override_id,
      historicalHeatOverrideUpdate: {
        expected_revision_id: override.versions.at(-1)?.revision_id ?? null,
        catalog:
          selectedRecipe === null
            ? null
            : {
                fixed_mix_set_id: selectedRecipe.fixed_mix_set_id,
                mix_id: selectedRecipe.mix_id,
              },
        reason,
        subreason,
        comment: commentChanged
          ? comment === ""
            ? null
            : comment
          : override.comment,
      },
    });
    if (response.data !== undefined) {
      onSaved(response.data);
    } else {
      setSubmitError(
        fetchErrorDetail(response.error) ?? t("overrideHistorySaveError")
      );
    }
  };
  return (
    <Dialog
      open
      onClose={saveState.isLoading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="edit-historical-override-title"
    >
      <DialogTitle id="edit-historical-override-title">
        {t("editHistoricalOverride")}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, pt: 1 }}>
          <Alert severity="info">{t("historicalOverrideCorrectionHint")}</Alert>
          <Typography variant="body2Mono">
            {override.scopes
              .map(
                (scope) => `${scope.stable_heat_id} (${scope.expected_grade})`
              )
              .join(", ")}
          </Typography>
          <Stack role="radiogroup" aria-label={t("recipe")} sx={{ gap: 1 }}>
            <OverrideRecipeCard
              title={override.recipe_name ?? t("overrideRecipeCurrent")}
              subtitle={t("historicalOverrideRecordedRecipe")}
              mix={override.override_mix}
              costDeltaPerTonne={null}
              selected={selectedRecipe === null}
              onSelect={() => {
                setSelectedRecipe(null);
              }}
            />
            {recipeOptions.map((recipe) => (
              <OverrideRecipeCard
                key={`${recipe.fixed_mix_set_id}/${recipe.mix_id}`}
                title={recipe.name}
                subtitle={null}
                mix={recipe.mix}
                costDeltaPerTonne={null}
                selected={
                  selectedRecipe?.fixed_mix_set_id ===
                    recipe.fixed_mix_set_id &&
                  selectedRecipe.mix_id === recipe.mix_id
                }
                onSelect={() => {
                  setSelectedRecipe(recipe);
                }}
              />
            ))}
          </Stack>
          {(recipes.isLoading || reasons.isLoading) && <LinearProgress />}
          {recipes.isError && (
            <RetryErrorAlert
              message={t("overrideHistoryRecipesError")}
              onRetry={() => {
                void recipes.refetch();
              }}
            />
          )}
          {reasons.isError && (
            <RetryErrorAlert
              message={t("overrideHistoryReasonsError")}
              onRetry={() => {
                void reasons.refetch();
              }}
            />
          )}
          <OverrideReasonFields
            reasonOptions={displayedReasons}
            reason={reason}
            subreason={subreason}
            comment={comment}
            commentRequired={commentRequired}
            onReasonChange={(nextReason) => {
              setReason(nextReason);
              setSubreason(null);
            }}
            onSubreasonChange={setSubreason}
            onCommentChange={setComment}
          />
          {submitError !== null && (
            <Alert severity="error">{submitError}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saveState.isLoading} onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          loading={saveState.isLoading}
          disabled={!canSave}
          onClick={() => {
            void submit();
          }}
        >
          {t("saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
